/* eslint-disable no-throw-literal */
import { create } from 'apisauce'
import { store } from '../store'
import { refreshToken } from '../services/AuthService'
import { authActions } from '../store/signIn'

/* TODO: FIX CAMEL CASE */
/* eslint-disable camelcase */

/**
 * Create an api of FTC API
 *
 * @type {ApisauceInstance}
 */
const api = create({
  baseURL: process.env.REACT_APP_FTC_API_SERVER_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    PORTAL: 'MANAGEMENT'
  },
  timeout: 30000
})

/**
 * Remove headers from API
 */
export const removeAuthorizationHeader = () => {
  api.deleteHeader('ORIGINATING-COMPANY-ID')
  api.deleteHeader('Authorization')
}

/**
 * Process response
 *
 * @param response
 * @returns {*}
 */
const processApiResponse = response => {
  if (!response.ok) {
    const title = `${response.status} - ${response.problem}`
    let message = `There was an error calling to FTC API: ${response.originalError}`

    if (response.data && response.data.error) {
      message = `${response.data.error.message}`
    }

    throw {
      name: title,
      message,
      code: response.status,
      details: response.data.error ?? null
    }
  }

  return response.data
}

/**
 * Performs a call to the BV API
 *
 * @param {string} type The REST method
 * @param {string} route The API URI
 * @param {object} params The parameters to be sent to the API
 * @param {number} attempt The attempt number
 *
 * @throws {object} Will throw an error if the access token could not be refreshed
 *
 * @returns {object} The API response data
 */
const callAPI = async (
  type,
  route,
  params = {},
  authorized = 1,
  attempt = 1
) => {
  const authStore = store.getState().auth
  let response
  if (authorized && authStore?.token?.access_token) {
    api.setHeader('Authorization', `Bearer ${authStore.token.access_token}`)
    if (authStore?.user?.userInfo) {
      api.setHeader(
        'ORIGINATING-COMPANY-ID',
        authStore.user.userInfo.originating_company
      )

      api.setHeader('CONFIG-ID', authStore.user.userInfo.config_id)
    }
  }

  switch (type) {
    case 'POST':
      response = await api.post(route, params)
      break
    case 'PUT':
      response = await api.put(route, params)
      break
    case 'DELETE':
      response = await api.delete(route, params)
      break
    case 'GET':
      response = await api.get(route, params)
      break
    case 'PATCH':
      response = await api.patch(route, params)
      break
    default:
      throw {
        name: 'Method Not Allowed',
        message: 'Call type not supported',
        code: 405
      }
  }
  if (!response.ok) {
    if (response.status === 401 && authorized) {
      if (attempt === 1 || attempt === 2) {
        attempt++
        try {
          await refreshToken(
            authStore.token.refresh_token,
            authStore.user.userId
          )
          return callAPI(type, route, params, authorized, attempt)
        } catch (error) {
          return callAPI(type, route, params, authorized, attempt)
        }
      } else {
        console.log('Unable to refresh token in callApi')
        store.dispatch(authActions.logout())
        if (response?.data?.error?.message?.toLowerCase() === 'invalid user') {
          store.dispatch(authActions.setInvalidUser())
        } else {
          store.dispatch(authActions.setForceLogout())
        }
        return processApiResponse(response)
      }
    }
  }
  return processApiResponse(response)
}

/**
 * Get an access token
 *
 * @param username
 * @param password
 * @returns {Promise<*>}
 */
export const apiLogin = async (username, password) => {
  return await callAPI(
    'POST',
    '/login',
    {
      username,
      password
    },
    false
  )
}

/**
 * Refresh an OAuth token through FTC API so the browser never receives the
 * OAuth client secret.
 *
 * @param refreshToken
 * @param userId
 * @returns {Promise<*>}
 */
export const apiRefreshToken = async (refreshToken, userId) => {
  return await callAPI(
    'POST',
    '/refresh-token',
    { refreshToken, userId },
    false
  )
}

/**
 * Send reset password request
 *
 * @param email
 * @returns {Promise<object>} The API response data
 */
export const resetPasswordRequest = async (email = '') => {
  return await callAPI('POST', '/users/forgotpassword', { email }, false)
}

/**
 * Get User Scopes
 *
 * @param email
 * @returns {Promise<object[]>} The API response data
 */
export const getUserScopes = async (email = '') => {
  return await callAPI(
    'GET',
    '/api/users/scopes',
    new URLSearchParams({
      email
    }),
    0
  )
}

/**
 * Get User
 *
 * @returns {Promise<object>} The API response data
 */
export const getUser = async () => {
  return await callAPI('GET', '/users/me')
}

/**
 * Create user
 *
 * @returns {Promise<object>} The API response data
 */
export const createUser = async (params, step) => {
  const response = await callAPI('POST', `/users?step=${step}`, params, false)
  if (!response || response.status === 204) return true
  return response
}

/**
 * Validate access code
 *
 * @returns {Promise<object>} The API response data
 */
export const validateAccessCode = async id => {
  return await callAPI('GET', '/users/validateaccesscode', { id }, false)
}

/*
 * Verify registration status
 *
 * @returns {Promise<boolean>} The API response data
 */
export const verifyRegistration = async email => {
  return await callAPI('GET', `/api/users/verify-registration/${email}`, {}, 0)
}

/**
 * Request access
 *
 * @param companyDomain
 * @param firstName
 * @param lastName
 * @param email
 * @returns {Promise<boolean>}
 */
export const requestAccess = async (
  companyDomain,
  firstName,
  lastName,
  email,
  companyName
) => {
  api.setHeader('Content-Type', 'application/json')
  return await callAPI(
    'POST',
    '/api/users/request-access',
    {
      company_domain: companyDomain,
      first_name: firstName,
      last_name: lastName,
      email,
      company_name: companyName
    },
    0
  )
}

/**
 * Update Account Settings
 *
 * @returns {Promise<object>} The API response data
 */
export const updateAccountSettings = async params => {
  const response = await callAPI('PUT', '/users/me', params, true)
  if (!response || response.status === 204) return true
  return response
}

/**
 * Update User
 *
 * @returns {Promise<object>} The API response data
 */
export const updateUser = async (id, params) => {
  const response = await callAPI('PUT', `/users/${id}`, params, true)
  if (!response || response.status === 204) return true
  return response
}

/**
 * Delete User
 */
export const deleteUser = async (id, companyId) => {
  const response = await callAPI('POST', '/users/deleteuser', {
    id,
    company_id: companyId
  })
  return response
}

/**
 * GET Selected company
 * @returns Company
 */
export const getCompany = async id => {
  const response = await callAPI('GET', `/companies/${id}`)
  return response
}

/**
 * GET Selected company Profile
 * @returns CompanyProfile
 */
export const getCompanyProfile = async id => {
  const response = await callAPI('GET', `/companyProfile/${id}`)
  return response
}
/**
 * PUT Update selected company
 * @returns Company
 */
export const updateCompany = async (id, params) => {
  const response = await callAPI('PUT', `/companies/${id}`, params)
  return response
}

/**
 * Get client users
 *
 * @param companyId
 * @returns {Promise<object[]>} The API response data
 */
export const getCompanyUsers = async (companyId = 0) => {
  return await callAPI('GET', `users/companyusers/${companyId}`)
}

/**
 * Get company roles
 *
 * @param companyId
 * @returns {Promise<object[]>} The API response data
 */
export const getRoles = async (companyId = 0) => {
  return await callAPI('GET', `roles/company/${companyId}`)
}

/**
 * Get company roles
 *
 * @param companyId
 * @returns {Promise<object[]>} The API response data
 */
export const getManagementRoles = async () => {
  return await callAPI('GET', 'roles/management')
}

/**
 * GET Company Roles list
 * @returns Company Roles list
 */
export const getCompanyRoles = async companyId => {
  try {
    const response = await callAPI('GET', `/roles/company/${companyId}`)
    return response
  } catch {
    return false
  }
}

/**
 * Change user password
 * @param password
 * @returns {Promise<object>} The API response data
 */
export const changeUserPassword = async password => {
  api.setHeader('Content-Type', 'application/json')
  return await callAPI('PUT', '/api/users/change-password', {
    password
  })
}

/**
 * GET Screen Configurations
 * @returns CompanyProfile
 */
export const getScreenConfigs = async (configId,
  page = 1,
  limit = 50,
  search = ''
) => {
  const queryParams = new URLSearchParams({
    config_id: configId,
    page: page.toString(),
    limit: limit.toString(),
    search
  })
  const response = await callAPI(
    'GET',
    `/screen-configurations?${queryParams.toString()}`
  )

  return response
}

/**
 * GET Trades
 * @returns trades
 */
export const getTrades = async id => {
  const response = await callAPI('GET', '/trades/wotrades')
  return response
}

/**
 * GET Work Order Types
 * @returns trades
 */
export const getWorkOrderTypes = async id => {
  const response = await callAPI('GET', '/trades/workordertypes')
  return response
}

/**
 * Create new screen configuration
 *
 * @returns new screen configuration
 */
export const createScreenConfiguration = async params => {
  const response = await callAPI('POST', '/screen-configurations', params)
  return response
}

/**
 * PUT screen configurations
 * @returns modified screen config
 */
export const updateScreenConfiguration = async (id, params) => {
  const response = await callAPI('PUT', `/screen-configurations/${id}`, params)
  return response
}

/**
 * GET screen configurations audis
 * @returns screen configurations audis
 */
export const getAudits = async id => {
  const response = await callAPI('GET', `/audits/${id}/screen-config`)
  return response
}

/**
 * POST adit
 * @returns new audit
 */
export const createAudit = async params => {
  const response = await callAPI('POST', '/audits', params)
  return response
}

/**
 * Put migrate-audits
 * @returns void
 */
export const migrateAudits = async params => {
  const response = await callAPI('PUT', '/audits/migrate', params)
  return response
}

/**
 * POST send portal invitation
 * @returns company profile
 */
export const sendPortalInvitation = async (email, companyId) => {
  try {
    const response = await callAPI('GET', 'users/sendInviteEmail', {
      email,
      companyId
    })
    return response
  } catch {
    return false
  }
}

export const getTimeSheetEntryFields = async () => {
  try {
    return await callAPI('GET', '/time-sheets/entry/fields')
  } catch (error) {
    console.error(error)
    return null
  }
}
